/* eslint-disable no-undef */
import { batch } from 'react-redux';

import {
  setFollowMe,
  setStartMutedPolicy,
  setStartReactionsMuted
} from '../base/conference/actions';
import i18next from '../base/i18n/i18next';
import { updateSettings } from '../base/settings/actions';
import { sendMessage } from '../chat/actions';
import { setScreenshareFramerate } from '../screen-share/actions';
import { handleToggleVideoMuted } from '../toolbox/actions';

import {
  OPEN_GROUPS,
  OPEN_SETTINGS,
  SET_AUDIO_SETTINGS_VISIBILITY,
  SET_SETTINGS,
  SET_VIDEO_ONLY,
  SET_VIDEO_SETTINGS_VISIBILITY
} from './actionTypes';
import {
  getModeratorTabProps,
  getMoreTabProps,
  getProfileTabProps,
  getSoundsTabProps
} from './functions';
import { hideDialog, openDialog } from '../base/dialog/actions';
import SleepMode from 'component/Mode/SleepMode';
import { getLocalVideoTrack } from '../base/tracks/functions';
import { toggleBackgroundEffect } from '../virtual-background/actions';
import virtualBackgroundLogger from '../virtual-background/logger';
import { joinRotation, leaveRotation, sendStatistics } from '../login/actions';

/**
 * Opens {@code LogoutDialog}.
 *
 * @param {Function} onLogout - The event in {@code LogoutDialog} that should be
 *  enabled on click.
 * @returns {Function}
 */
export function openLogoutDialog() {
  // return openDialog(LogoutDialog, { onLogout });
}

/**
 * Opens {@code SettingsDialog}.
 *
 * @param {string} defaultTab - The tab in {@code SettingsDialog} that should be
 * displayed initially.
 * @param {boolean} isDisplayedOnWelcomePage - Indicates whether the device selection dialog is displayed on the
 * welcome page or not.
 * @returns {Function}
 */
export function openSettingsDialog() {
  // return openSheet(SettingsScreen);
}

/**
 * Sets the visibility of the audio settings.
 *
 * @param {boolean} value - The new value.
 * @returns {Function}
 */
function setAudioSettingsVisibility(value) {
  return {
    type: SET_AUDIO_SETTINGS_VISIBILITY,
    value
  };
}

/**
 * Sets the visibility of the audio settings.
 *
 * @param {boolean} value - The new value.
 * @returns {Function}
 */
export function setVideoOnly(value) {
  return {
    type: SET_VIDEO_ONLY,
    value
  };
}
/**
 * Sets the visibility of the video settings.
 *
 * @param {boolean} value - The new value.
 * @returns {Function}
 */
function setVideoSettingsVisibility(value) {
  return {
    type: SET_VIDEO_SETTINGS_VISIBILITY,
    value
  };
}

export function enableSleepMode(
  hours,
  minutes,
  isFromPrivacy = false,
  isFromExternalCall = false,
  isSleepMode = false
) {
  return (dispatch, getState) => {
    const state = getState();
    const { conference } = state['features/base/conference'];
    const localParticipant = state['features/base/participants'].local;
    dispatch({
      type: SET_SETTINGS,
      data: {
        sleepHours: hours,
        sleepMinutes: minutes,
        userSleepMode: isSleepMode,
        userPrivacyMode: isFromPrivacy,
        userExternalMode: isFromExternalCall,
        userDigitalSignageMode: false
      }
    });
    if (isFromExternalCall) {
      conference?.sendCommand('customUserExternal', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserExternal' },
        value: 'on'
      });

      dispatch(
        sendMessage(
          JSON.stringify({
            EventType: 12,
            Message: 'External Call On',
            senderID: localParticipant?.id,
            FromParticipantID: localParticipant?.id
          })
        )
      );
      dispatch(
        openDialog(SleepMode, 'SleepMode', { isExternalCallActive: true })
      );
    }
    if (isFromPrivacy) {
      conference?.sendCommand('customUserPrivacy', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserPrivacy' },
        value: 'on'
      });

      dispatch(
        sendMessage(
          JSON.stringify({
            EventType: 8,
            Message: 'Privacy On',
            senderID: localParticipant?.id,
            FromParticipantID: localParticipant?.id
          })
        )
      );
      const ms =
        ((parseInt(hours) > 0 ? parseInt(hours) * 60 : 0) + parseInt(minutes)) *
        60000;
      if (window.sleepTimeout) {
        clearTimeout(window.sleepTimeout);
        window.sleepTimeout = undefined;
      }
      window.setTimeout(() => disableSleepMode(), ms);
      dispatch(openDialog(SleepMode, 'SleepMode', { isPrivacySleep: true }));
    } else {
      conference?.sendCommand('customSleepModeStats', {
        attributes: {
          xmlns: 'http://jabber.org/protocol/customSleepModeStats'
        },
        value: 'on'
      });
      dispatch(openDialog(SleepMode, 'SleepMode'));
    }
    dispatch(handleToggleVideoMuted(true, true, true));
    dispatch(leaveRotation());
    APP.conference.handleJoinLeaveAudio('off');
    if (window.digitalSignageInterval) {
      clearInterval(window.digitalSignageInterval);
      window.digitalSignageInterval = undefined;
    }
    if (window.statsInterval) {
      clearInterval(window.statsInterval);
      window.statsInterval = undefined;
    }
    window.statsInterval = setInterval(() => {
      const { localStats } = APP.store.getState()['features/base/conference'];
      if (localStats) {
        APP.store.dispatch(sendStatistics(localStats));
      }
    }, 20000);
  };
}

export function disableSleepMode(isSleepMode = false) {
  return (dispatch, getState) => {
    const state = getState();
    const { conference } = state['features/base/conference'];
    const { room } = getState()['features/base/settings'];
    const { userPrivacyMode, userExternalMode } = state['features/settings'];
    const localParticipant = state['features/base/participants'].local;
    // console.log('disableSleepMode', userPrivacyMode, userExternalMode);

    if (userExternalMode) {
      conference?.sendCommand('customUserExternal', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserExternal' },
        value: 'off'
      });

      dispatch(
        sendMessage(
          JSON.stringify({
            EventType: 13,
            Message: 'External Call Off',
            senderID: localParticipant?.id,
            FromParticipantID: localParticipant?.id
          })
        )
      );
      dispatch(updateSleepSettings({ userExternalMode: false }));
      APP.conference.handleEventsUpdateUI(true);
    }
    if (userPrivacyMode) {
      conference?.sendCommand('customUserPrivacy', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserPrivacy' },
        value: 'off'
      });
      dispatch(
        sendMessage(
          JSON.stringify({
            EventType: 9,
            Message: 'Privacy Off',
            senderID: localParticipant?.id,
            FromParticipantID: localParticipant?.id
          })
        )
      );

      dispatch(updateSleepSettings({ userPrivacyMode: false }));
      APP.conference.handleEventsUpdateUI(true);
    } else {
      conference?.sendCommand('customUserWakeUp', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserWakeUp' },
        value: 'yes'
      });
      conference?.sendCommand('customSleepModeStats', {
        attributes: { xmlns: 'http://jabber.org/protocol/customUserWakeUp' },
        value: 'off'
      });
      if (isSleepMode) {
        APP.conference.handleEventsUpdateUI(true, false);
      } else {
        if (window.sleepTimeout) {
          clearTimeout(window.sleepTimeout);
          window.sleepTimeout = undefined;
        }
        if (window.tempSleepTimeout) {
          clearTimeout(window.tempSleepTimeout);
          window.tempSleepTimeout = undefined;
        }
        APP.conference.handleEventsUpdateUI(true, false);
        window.tempSleepTimeout = setTimeout(() => {
          APP.conference.handleEventsUpdateUI(false);
          clearTimeout(window.tempSleepTimeout);
          window.tempSleepTimeout = undefined;
        }, 1800000);
      }
      dispatch(updateSleepSettings({ userSleepMode: false }));
    }
    dispatch(joinRotation(room));
    dispatch(hideDialog('SleepMode'));
    APP.conference.checkDigitalSignage();
  };
}

export function updateSleepSettings(data) {
  return {
    type: SET_SETTINGS,
    data
  };
}

export function toggleSettings(data) {
  return {
    type: OPEN_SETTINGS,
    data
  };
}

export function toggleGroups(data) {
  return {
    type: OPEN_GROUPS,
    data
  };
}

/**
 * Submits the settings from the "More" tab of the settings dialog.
 *
 * @param {Object} newState - The new settings.
 * @returns {Function}
 */
export function submitMoreTab(newState) {
  return (dispatch, getState) => {
    const currentState = getMoreTabProps(getState());

    const showPrejoinPage = newState.showPrejoinPage;

    if (showPrejoinPage !== currentState.showPrejoinPage) {
      dispatch(
        updateSettings({
          userSelectedSkipPrejoin: !showPrejoinPage
        })
      );
    }

    const enabledNotifications = newState.enabledNotifications;

    if (enabledNotifications !== currentState.enabledNotifications) {
      dispatch(
        updateSettings({
          userSelectedNotifications: {
            ...getState()['features/base/settings'].userSelectedNotifications,
            ...enabledNotifications
          }
        })
      );
    }

    if (newState.currentLanguage !== currentState.currentLanguage) {
      i18next.changeLanguage(newState.currentLanguage);
    }

    if (newState.currentFramerate !== currentState.currentFramerate) {
      const frameRate = parseInt(newState.currentFramerate, 10);

      dispatch(setScreenshareFramerate(frameRate));
    }

    if (newState.hideSelfView !== currentState.hideSelfView) {
      dispatch(updateSettings({ disableSelfView: newState.hideSelfView }));
    }

    if (newState.maxStageParticipants !== currentState.maxStageParticipants) {
      dispatch(
        updateSettings({
          maxStageParticipants: Number(newState.maxStageParticipants)
        })
      );
    }
  };
}

/**
 * Submits the settings from the "Moderator" tab of the settings dialog.
 *
 * @param {Object} newState - The new settings.
 * @returns {Function}
 */
export function submitModeratorTab(newState) {
  return (dispatch, getState) => {
    const currentState = getModeratorTabProps(getState());

    if (newState.followMeEnabled !== currentState.followMeEnabled) {
      dispatch(setFollowMe(newState.followMeEnabled));
    }

    if (newState.startReactionsMuted !== currentState.startReactionsMuted) {
      batch(() => {
        // updating settings we want to update and backend (notify the rest of the participants)
        dispatch(setStartReactionsMuted(newState.startReactionsMuted, true));
        dispatch(
          updateSettings({ soundsReactions: !newState.startReactionsMuted })
        );
      });
    }

    if (
      newState.startAudioMuted !== currentState.startAudioMuted ||
      newState.startVideoMuted !== currentState.startVideoMuted
    ) {
      dispatch(
        setStartMutedPolicy(newState.startAudioMuted, newState.startVideoMuted)
      );
    }
  };
}

/**
 * Submits the settings from the "Profile" tab of the settings dialog.
 *
 * @param {Object} newState - The new settings.
 * @returns {Function}
 */
export function submitProfileTab(newState) {
  return (dispatch, getState) => {
    const currentState = getProfileTabProps(getState());

    if (newState.displayName !== currentState.displayName) {
      APP.conference.changeLocalDisplayName(newState.displayName);
    }

    if (newState.email !== currentState.email) {
      APP.conference.changeLocalEmail(newState.email);
    }
  };
}

/**
 * Submits the settings from the "Sounds" tab of the settings dialog.
 *
 * @param {Object} newState - The new settings.
 * @returns {Function}
 */
export function submitSoundsTab(newState) {
  return (dispatch, getState) => {
    const currentState = getSoundsTabProps(getState());
    const shouldNotUpdateReactionSounds = getModeratorTabProps(
      getState()
    ).startReactionsMuted;
    const shouldUpdate =
      newState.soundsIncomingMessage !== currentState.soundsIncomingMessage ||
      newState.soundsParticipantJoined !==
        currentState.soundsParticipantJoined ||
      newState.soundsParticipantKnocking !==
        currentState.soundsParticipantKnocking ||
      newState.soundsParticipantLeft !== currentState.soundsParticipantLeft ||
      newState.soundsTalkWhileMuted !== currentState.soundsTalkWhileMuted ||
      newState.soundsReactions !== currentState.soundsReactions;

    if (shouldUpdate) {
      const settingsToUpdate = {
        soundsIncomingMessage: newState.soundsIncomingMessage,
        soundsParticipantJoined: newState.soundsParticipantJoined,
        soundsParticipantKnocking: newState.soundsParticipantKnocking,
        soundsParticipantLeft: newState.soundsParticipantLeft,
        soundsTalkWhileMuted: newState.soundsTalkWhileMuted,
        soundsReactions: newState.soundsReactions
      };

      if (shouldNotUpdateReactionSounds) {
        delete settingsToUpdate.soundsReactions;
      }
      dispatch(updateSettings(settingsToUpdate));
    }
  };
}

/**
 * Toggles the visibility of the audio settings.
 *
 * @returns {void}
 */
export function toggleAudioSettings() {
  return (dispatch, getState) => {
    const value = getState()['features/settings'].audioSettingsVisible;

    dispatch(setAudioSettingsVisibility(!value));
  };
}

/**
 * Toggles the visibility of the video settings.
 *
 * @returns {void}
 */
export function toggleVideoSettings() {
  return (dispatch, getState) => {
    const value = getState()['features/settings'].videoSettingsVisible;

    dispatch(setVideoSettingsVisibility(!value));
  };
}

/**
 * Submits the settings from the "Virtual Background" tab of the settings dialog.
 *
 * @param {Object} newState - The new settings.
 * @param {boolean} isCancel - Whether the change represents a cancel.
 * @returns {Function}
 */
export function submitVirtualBackgroundTab(newState, isCancel = false) {
  return async (dispatch, getState) => {
    const state = getState();
    const track = getLocalVideoTrack(state['features/base/tracks'])?.jitsiTrack;

    if (newState.options?.selectedThumbnail) {
      await dispatch(toggleBackgroundEffect(newState.options, track));

      if (!isCancel) {
        // Set x scale to default value.
        dispatch(
          updateSettings({
            localFlipX: true
          })
        );

        virtualBackgroundLogger.info(
          `Virtual background type: '${
            typeof newState.options.backgroundType === 'undefined'
              ? 'none'
              : newState.options.backgroundType
          }' applied!`
        );
      }
    }
  };
}
